import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties, type MouseEvent as ReactMouseEvent } from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import DOMPurify from "dompurify";
import {
  api,
  type MailFolder,
  type MailMessageDetail,
  type MailMessageSummary,
  type Preferences,
  type PublicMailAccount,
} from "../../lib/api";
import { announceNewMail } from "../../lib/notifications";
import { formatSnoozeUntil, isSnoozed, loadSnoozed, pruneSnoozed, saveSnoozed, snoozePresets } from "../../lib/snooze";
import { useToast } from "../shell/Toast";
import { useConfirm } from "../shell/ConfirmDialog";
import { ComposerModal, type ComposeMode } from "./ComposerModal";
import { AttachmentPreviewModal } from "./AttachmentPreviewModal";
import { AccountWizard } from "./AccountWizard";
import { FolderManagerModal } from "./FolderManagerModal";
import { MsgRowQuickActions } from "./MsgRowQuickActions";

let purifyHooksInstalled = false;
function ensureMailPurifyHooks() {
  if (purifyHooksInstalled) return;
  purifyHooksInstalled = true;
  DOMPurify.addHook("afterSanitizeAttributes", (node) => {
    if (node.nodeName === "A") {
      const el = node as HTMLElement;
      el.setAttribute("target", "_blank");
      el.setAttribute("rel", "noopener noreferrer");
      const href = el.getAttribute("href") || "";
      if (/^\s*javascript:/i.test(href) || /^\s*data:/i.test(href)) {
        el.removeAttribute("href");
      }
    }
  });
}

type MobilePane = "nav" | "list" | "read";
type SmartId = "" | "unread" | "starred" | "attachments" | "today";
type SortId =
  | "date-desc"
  | "date-asc"
  | "from-asc"
  | "from-desc"
  | "subject-asc"
  | "subject-desc";
type DateFilterId = "all" | "today" | "yesterday" | "7d" | "30d" | "month" | "custom";

function loadPref<T extends string>(key: string, fallback: T): T {
  try {
    return (localStorage.getItem(key) as T) || fallback;
  } catch {
    return fallback;
  }
}

function loadWidth(key: string, fallback: number, min: number, max: number): number {
  try {
    const n = Number(localStorage.getItem(key));
    if (!Number.isFinite(n)) return fallback;
    return Math.min(max, Math.max(min, n));
  } catch {
    return fallback;
  }
}

function messageKey(m: { accountId: string; folder: string; uid: number }) {
  return `${m.accountId}:${m.folder}:${m.uid}`;
}

export function MailWorkspace({ prefs }: { prefs: Preferences | null }) {
  const toast = useToast();
  const { confirm } = useConfirm();
  const [accounts, setAccounts] = useState<PublicMailAccount[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<string | "all">("all");
  const [folders, setFolders] = useState<MailFolder[]>([]);
  const [folder, setFolder] = useState("INBOX");
  const [smart, setSmart] = useState<SmartId>("");
  const [messages, setMessages] = useState<MailMessageSummary[]>([]);
  const [total, setTotal] = useState(0);
  const [selected, setSelected] = useState<MailMessageSummary | null>(null);
  const [detail, setDetail] = useState<MailMessageDetail | null>(null);
  const [search, setSearch] = useState("");
  const [committedSearch, setCommittedSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [blockingLoad, setBlockingLoad] = useState(false);
  const [error, setError] = useState("");
  const [composer, setComposer] = useState(false);
  const [composeMode, setComposeMode] = useState<ComposeMode>("new");
  const [folderMgrOpen, setFolderMgrOpen] = useState(false);
  const [moveMenuOpen, setMoveMenuOpen] = useState(false);
  const [readMoveMenuOpen, setReadMoveMenuOpen] = useState(false);
  const [readMoveFolders, setReadMoveFolders] = useState<MailFolder[]>([]);
  const [readMoveBusy, setReadMoveBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [wizard, setWizard] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState<{
    accountId: string;
    folder: string;
    uid: number;
    attachment: { filename: string; contentType: string; size: number; partId: string };
  } | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [mobile, setMobile] = useState<MobilePane>("list");
  const [railExpanded, setRailExpanded] = useState(() => {
    try {
      return localStorage.getItem("mailstorm.railExpanded") === "1";
    } catch {
      return true;
    }
  });
  const [sortBy, setSortBy] = useState<SortId>(() => loadPref("mailstorm.sortBy", "date-desc"));
  const [dateFilter, setDateFilter] = useState<DateFilterId>(() => loadPref("mailstorm.dateFilter", "all"));
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [snoozed, setSnoozed] = useState(() => pruneSnoozed(loadSnoozed()));
  const [snoozeMenuKey, setSnoozeMenuKey] = useState<string | null>(null);
  const [folderWidth, setFolderWidth] = useState(() => loadWidth("mailstorm.folderWidth", 220, 160, 420));
  const [listWidth, setListWidth] = useState(() => loadWidth("mailstorm.listWidth", 460, 280, 720));
  const [dragAccountId, setDragAccountId] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const workspaceRef = useRef<HTMLDivElement>(null);
  const loadGen = useRef(0);
  const defaultApplied = useRef(false);
  const resizing = useRef<"folder" | "list" | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem("mailstorm.railExpanded", railExpanded ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, [railExpanded]);

  useEffect(() => {
    try {
      localStorage.setItem("mailstorm.sortBy", sortBy);
      localStorage.setItem("mailstorm.dateFilter", dateFilter);
      localStorage.setItem("mailstorm.folderWidth", String(folderWidth));
      localStorage.setItem("mailstorm.listWidth", String(listWidth));
    } catch {
      /* ignore */
    }
  }, [sortBy, dateFilter, folderWidth, listWidth]);

  useEffect(() => {
    const t = window.setInterval(() => {
      setSnoozed((prev) => {
        const next = pruneSnoozed(prev);
        if (Object.keys(next).length !== Object.keys(prev).length) saveSnoozed(next);
        return next;
      });
    }, 60_000);
    return () => window.clearInterval(t);
  }, []);

  const snoozePresetList = useMemo(() => snoozePresets(), [snoozeMenuKey]);

  const refreshAccounts = useCallback(async () => {
    try {
      const res = await api.accounts();
      setAccounts(res.accounts);
      if (!defaultApplied.current && prefs?.defaultAccountId) {
        defaultApplied.current = true;
        setSelectedAccountId(prefs.defaultAccountId);
      } else if (!defaultApplied.current) {
        defaultApplied.current = true;
      }
    } catch (e) {
      setAccounts([]);
      toast.push({
        message: e instanceof Error ? e.message : "Impossibile caricare gli account",
      });
    }
  }, [prefs?.defaultAccountId, toast]);

  const loadFoldersFor = useCallback(async (accountId: string | "all") => {
    if (accountId === "all") {
      setFolders([
        { path: "INBOX", name: "Inbox", specialUse: "\\Inbox", delimiter: "/", listed: true, subscribed: true },
        { path: "Sent", name: "Sent", specialUse: "\\Sent", delimiter: "/", listed: true, subscribed: true },
        { path: "Drafts", name: "Drafts", specialUse: "\\Drafts", delimiter: "/", listed: true, subscribed: true },
        { path: "Archive", name: "Archive", specialUse: "\\Archive", delimiter: "/", listed: true, subscribed: true },
        { path: "Trash", name: "Trash", specialUse: "\\Trash", delimiter: "/", listed: true, subscribed: true },
        { path: "Junk", name: "Spam", specialUse: "\\Junk", delimiter: "/", listed: true, subscribed: true },
      ]);
      return;
    }
    try {
      const res = await api.folders(accountId);
      setFolders(res.folders);
    } catch {
      setFolders([]);
    }
  }, []);

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadMoreLock = useRef(false);

  const loadMessagesFor = useCallback(
    async (
      accountId: string | "all",
      folderPath: string,
      smartId: SmartId,
      searchQ: string,
      opts?: { bustCache?: boolean; page?: number; append?: boolean },
    ) => {
      const pageNum = opts?.page ?? 1;
      const res = await api.messages({
        accountId: accountId === "all" ? undefined : accountId,
        folder: folderPath,
        page: pageNum,
        pageSize: 60,
        search: searchQ.trim() || undefined,
        smart: smartId || undefined,
        bustCache: opts?.bustCache,
      });
      if (opts?.append) {
        setMessages((prev) => {
          const seen = new Set(prev.map(messageKey));
          const extra = res.messages.filter((m) => !seen.has(messageKey(m)));
          const merged = [...prev, ...extra];
          // Keep loading while the server still returns rows and we haven't covered total
          setHasMore(extra.length > 0 && (merged.length < res.total || res.messages.length >= 60));
          return merged;
        });
      } else {
        setMessages(res.messages);
        setHasMore(res.messages.length > 0 && (res.messages.length < res.total || res.messages.length >= 60));
      }
      setTotal(res.total);
      setPage(pageNum);
    },
    [],
  );

  const refreshMailbox = useCallback(
    async (opts?: { blocking?: boolean; bustCache?: boolean; searchQ?: string }) => {
      const gen = ++loadGen.current;
      if (opts?.blocking) setBlockingLoad(true);
      setBusy(true);
      setError("");
      setHasMore(true);
      setPage(1);
      const q = opts?.searchQ !== undefined ? opts.searchQ : committedSearch;
      try {
        // Parallel: folders + messages (was sequential — felt twice as slow)
        await Promise.all([
          loadFoldersFor(selectedAccountId),
          loadMessagesFor(selectedAccountId, folder, smart, q, {
            bustCache: opts?.bustCache,
            page: 1,
            append: false,
          }),
        ]);
        if (gen !== loadGen.current) return;
      } catch (e) {
        if (gen !== loadGen.current) return;
        setError(e instanceof Error ? e.message : String(e));
        setMessages([]);
        setTotal(0);
      } finally {
        if (gen === loadGen.current) {
          setBusy(false);
          setBlockingLoad(false);
        }
      }
    },
    [committedSearch, folder, loadFoldersFor, loadMessagesFor, selectedAccountId, smart],
  );

  useEffect(() => {
    void refreshAccounts();
  }, [refreshAccounts]);

  useEffect(() => {
    void refreshMailbox();
  }, [refreshMailbox]);

  const unseenBaseline = useRef<Map<string, number> | null>(null);
  const notifyReady = useRef(false);
  const folderRef = useRef(folder);
  folderRef.current = folder;

  function selectAccount(id: string | "all") {
    if (id === selectedAccountId) return;
    setBlockingLoad(true);
    setBusy(true);
    setSelectedAccountId(id);
    setFolder("INBOX");
    setSmart("");
    setSelected(null);
    setDetail(null);
    setMessages([]);
    setTotal(0);
    setSelectedIds(new Set());
    setMobile("list");
    setError("");
  }

  function startColResize(which: "folder" | "list", e: ReactMouseEvent) {
    e.preventDefault();
    resizing.current = which;
    const startX = e.clientX;
    const startFolder = folderWidth;
    const startList = listWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";

    function onMove(ev: MouseEvent) {
      const dx = ev.clientX - startX;
      if (resizing.current === "folder") {
        setFolderWidth(Math.min(420, Math.max(160, startFolder + dx)));
      } else if (resizing.current === "list") {
        setListWidth(Math.min(720, Math.max(280, startList + dx)));
      }
    }
    function onUp() {
      resizing.current = null;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    }
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }

  async function onAccountDrop(targetId: string) {
    if (!dragAccountId || dragAccountId === targetId) {
      setDragAccountId(null);
      setDragOverId(null);
      return;
    }
    const ids = accounts.map((a) => a.id);
    const from = ids.indexOf(dragAccountId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(from, 1);
    ids.splice(to, 0, dragAccountId);
    const reordered = ids
      .map((id) => accounts.find((a) => a.id === id)!)
      .filter(Boolean)
      .map((a, i) => ({ ...a, sortOrder: i }));
    setAccounts(reordered);
    setDragAccountId(null);
    setDragOverId(null);
    try {
      const res = await api.reorderAccounts(ids);
      setAccounts(res.accounts);
      toast.push({ message: "Ordine account salvato" });
    } catch (e) {
      toast.push({ message: e instanceof Error ? e.message : "Riordino fallito" });
      await refreshAccounts();
    }
  }

  const refreshMessages = useCallback(
    async (opts?: { bustCache?: boolean }) => {
      const q = search.trim();
      setCommittedSearch(q);
      await refreshMailbox({ bustCache: opts?.bustCache ?? true, searchQ: q });
    },
    [refreshMailbox, search],
  );

  // Watch accounts + poll for new mail → sound + desktop notification
  useEffect(() => {
    if (!accounts.length) return;
    const ids = accounts.map((a) => a.id).slice(0, 15);
    let cancelled = false;
    const accountEmails = new Map(accounts.map((a) => [a.id, a.email]));

    async function tick(forceImap: boolean) {
      try {
        const st = forceImap ? await api.idleRefresh() : await api.idleStatus();
        if (cancelled) return;
        const map = new Map<string, number>();
        let grew = 0;
        let grewAccount = "";
        for (const s of st.snapshots) {
          if (s.unseen < 0) continue;
          map.set(s.accountId, s.unseen);
          const prev = unseenBaseline.current?.get(s.accountId);
          if (typeof prev === "number" && s.unseen > prev) {
            grew += s.unseen - prev;
            if (!grewAccount) grewAccount = accountEmails.get(s.accountId) || "";
          }
        }
        if (!unseenBaseline.current) {
          unseenBaseline.current = map;
          notifyReady.current = true;
          return;
        }
        if (notifyReady.current && grew > 0) {
          announceNewMail(grew, grewAccount);
          if (folderRef.current === "INBOX") {
            void refreshMessages({ bustCache: true });
          }
        }
        unseenBaseline.current = map;
      } catch {
        /* ignore poll errors */
      }
    }

    void api.idleWatch(ids, true).catch(() => undefined);
    void tick(true);
    const light = window.setInterval(() => void tick(false), 45_000);
    const heavy = window.setInterval(() => void tick(true), 120_000);

    function onFocusInbox() {
      setFolder("INBOX");
      setSmart("");
      setMobile("list");
      void refreshMessages({ bustCache: true });
    }
    window.addEventListener("mailstorm:focus-inbox", onFocusInbox);

    return () => {
      cancelled = true;
      window.clearInterval(light);
      window.clearInterval(heavy);
      window.removeEventListener("mailstorm:focus-inbox", onFocusInbox);
    };
  }, [accounts, refreshMessages]);

  useEffect(() => {
    function onCompose() {
      openCompose("new");
    }
    window.addEventListener("mailstorm:compose", onCompose);
    return () => window.removeEventListener("mailstorm:compose", onCompose);
  }, []);

  function openCompose(mode: ComposeMode) {
    if (mode !== "new" && !detail) {
      toast.push({ message: "Apri un messaggio e attendi il caricamento prima di rispondere o inoltrare" });
      return;
    }
    setComposeMode(mode);
    setComposer(true);
  }

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === "j") moveSelection(1);
      if (e.key === "k") moveSelection(-1);
      if (e.key === "c" && !e.metaKey && !e.ctrlKey) openCompose("new");
      if (e.key === "r" && selected) openCompose("reply");
      if (e.key === "a" && selected) openCompose("replyAll");
      if (e.key === "f" && selected) openCompose("forward");
      if (e.key === "e" && selected) void archiveSelected();
      if (e.key === "#" && selected) void trashSelected();
      if (e.key === "!" && selected) void spamSelected();
      if (e.key === "s" && selected) void toggleStar();
      if (e.key === "u" && selected) void toggleSeen();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  function keyOf(m: MailMessageSummary) {
    return messageKey(m);
  }

  async function loadMoreMessages() {
    if (loadMoreLock.current || loadingMore || !hasMore || busy) return;
    loadMoreLock.current = true;
    setLoadingMore(true);
    try {
      await loadMessagesFor(selectedAccountId, folder, smart, committedSearch, {
        page: page + 1,
        append: true,
        bustCache: true,
      });
    } catch (e) {
      toast.push({ message: e instanceof Error ? e.message : "Impossibile caricare altri messaggi" });
    } finally {
      setLoadingMore(false);
      loadMoreLock.current = false;
    }
  }

  function moveSelection(delta: number) {
    if (!viewMessages.length) return;
    const idx = selected
      ? viewMessages.findIndex((m) => keyOf(m) === keyOf(selected))
      : -1;
    const next = viewMessages[Math.max(0, Math.min(viewMessages.length - 1, idx + delta))];
    if (next) void openMessage(next);
  }

  async function openMessage(msg: MailMessageSummary) {
    setReadMoveMenuOpen(false);
    setSelected(msg);
    setMobile("read");
    setDetail(null);
    try {
      const res = await api.message(msg.accountId, msg.folder, msg.uid);
      setDetail(res.message);
      const seen = Boolean(res.message.seen);
      setSelected({ ...msg, ...res.message, seen });
      setMessages((prev) =>
        prev.map((m) => (keyOf(m) === keyOf(msg) ? { ...m, seen } : m)),
      );
      if (!msg.seen && !seen) {
        toast.push({ message: "Attenzione: stato «letto» non salvato sul server IMAP" });
      }
      const invite = (res as { invite?: { title?: string; uid?: string } | null }).invite;
      if (invite?.title) {
        toast.push({
          message: `Invite: ${invite.title}`,
          actionLabel: "Accept",
          onAction: () => {
            void (async () => {
              const cals = await api.calendars();
              const calendarId = cals.calendars[0]?.id;
              if (!calendarId) {
                toast.push({ message: "Add a calendar first" });
                return;
              }
              const start = new Date();
              const end = new Date(start.getTime() + 60 * 60 * 1000);
              await api.acceptInvite({
                calendarId,
                title: invite.title,
                start: start.toISOString(),
                end: end.toISOString(),
                uid: invite.uid,
              });
              toast.push({ message: "Invite accepted to calendar" });
            })();
          },
        });
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }

  async function toggleStar() {
    if (!selected) return;
    const flagged = !selected.flagged;
    await api.flags({ accountId: selected.accountId, folder: selected.folder, uid: selected.uid, flagged });
    setSelected({ ...selected, flagged });
    setMessages((prev) =>
      prev.map((m) => (keyOf(m) === keyOf(selected) ? { ...m, flagged } : m)),
    );
  }

  async function setMessageSeen(msg: MailMessageSummary, seen: boolean) {
    if (msg.seen === seen) return;
    const k = keyOf(msg);
    try {
      await api.flags({ accountId: msg.accountId, folder: msg.folder, uid: msg.uid, seen });
      setMessages((prev) => prev.map((m) => (keyOf(m) === k ? { ...m, seen } : m)));
      if (selected && keyOf(selected) === k) {
        setSelected({ ...selected, seen });
        setDetail((d) => (d && keyOf(d) === k ? { ...d, seen } : d));
      }
    } catch (e) {
      toast.push({ message: e instanceof Error ? e.message : "Impossibile aggiornare lo stato" });
    }
  }

  async function setSeenState(seen: boolean) {
    if (!selected) return;
    if (selected.seen === seen) {
      toast.push({ message: seen ? "Già segnata come letta" : "Già segnata come da leggere" });
      return;
    }
    await setMessageSeen(selected, seen);
    toast.push({ message: seen ? "Segnata come letta" : "Segnata come da leggere" });
  }

  async function toggleSeen() {
    if (!selected) return;
    await setSeenState(!selected.seen);
  }

  function isDraftsFolder(path: string) {
    const f = folders.find((fl) => fl.path === path);
    if (f?.specialUse === "\\Drafts") return true;
    if (/drafts|bozze/i.test(path) || /drafts|bozze/i.test(f?.name || "")) return true;
    return false;
  }

  function findFolder(kind: "archive" | "trash" | "spam") {
    if (kind === "archive") {
      return folders.find((f) => f.specialUse === "\\Archive" || /archive|tutti i messaggi/i.test(f.name))?.path || "Archive";
    }
    if (kind === "trash") {
      return (
        folders.find((f) => f.specialUse === "\\Trash" || /trash|cestino|deleted/i.test(f.name))?.path || "Trash"
      );
    }
    return folders.find((f) => f.specialUse === "\\Junk" || /junk|spam/i.test(f.name))?.path || "Junk";
  }

  async function moveMessage(msg: MailMessageSummary, kind: "archive" | "trash" | "spam", label: string) {
    const snapshot = msg;
    const k = keyOf(snapshot);
    const touchingOpen = Boolean(selected && keyOf(selected) === k);
    setMessages((prev) => prev.filter((m) => keyOf(m) !== k));
    setTotal((t) => Math.max(0, t - 1));
    if (touchingOpen) {
      setSelected(null);
      setDetail(null);
    }
    try {
      // Resolve Trash/Archive/Spam on the server from IMAP LIST (client folder list can be stale/wrong)
      const moved = await api.move({
        accountId: snapshot.accountId,
        folder: snapshot.folder,
        uid: snapshot.uid,
        kind,
      });
      const undoFrom = moved.targetFolder;
      const undoUid = moved.destinationUid || snapshot.uid;
      toast.push({
        message: label,
        actionLabel: "Annulla",
        onAction: () => {
          void api
            .undo({
              accountId: snapshot.accountId,
              fromFolder: undoFrom,
              toFolder: snapshot.folder,
              uid: undoUid,
            })
            .then(() => {
              toast.push({ message: "Azione annullata" });
              void refreshMessages({ bustCache: true });
            })
            .catch((e) => {
              toast.push({ message: e instanceof Error ? e.message : "Annulla non riuscito" });
              void refreshMessages({ bustCache: true });
            });
        },
      });
      if (selectedAccountId !== "all") void loadFoldersFor(selectedAccountId);
    } catch (e) {
      toast.push({ message: e instanceof Error ? e.message : String(e) });
      void refreshMessages({ bustCache: true });
    }
  }

  async function moveSelected(kind: "archive" | "trash" | "spam", label: string) {
    if (!selected) return;
    await moveMessage(selected, kind, label);
  }

  async function moveToFolder(msg: MailMessageSummary, targetFolder: string) {
    const snapshot = msg;
    const k = keyOf(snapshot);
    const touchingOpen = Boolean(selected && keyOf(selected) === k);
    setReadMoveBusy(true);
    setReadMoveMenuOpen(false);
    setMoveMenuOpen(false);
    setMessages((prev) => prev.filter((m) => keyOf(m) !== k));
    setTotal((t) => Math.max(0, t - 1));
    if (touchingOpen) {
      setSelected(null);
      setDetail(null);
    }
    try {
      const moved = await api.move({
        accountId: snapshot.accountId,
        folder: snapshot.folder,
        uid: snapshot.uid,
        targetFolder,
      });
      toast.push({
        message: `Spostato in «${moved.targetFolder || targetFolder}»`,
        actionLabel: "Annulla",
        onAction: () => {
          void api
            .undo({
              accountId: snapshot.accountId,
              fromFolder: moved.targetFolder || targetFolder,
              toFolder: snapshot.folder,
              uid: moved.destinationUid || snapshot.uid,
            })
            .then(() => {
              toast.push({ message: "Azione annullata" });
              void refreshMessages({ bustCache: true });
            })
            .catch((e) => {
              toast.push({ message: e instanceof Error ? e.message : "Annulla non riuscito" });
              void refreshMessages({ bustCache: true });
            });
        },
      });
      if (selectedAccountId !== "all") void loadFoldersFor(selectedAccountId);
    } catch (e) {
      toast.push({ message: e instanceof Error ? e.message : String(e) });
      void refreshMessages({ bustCache: true });
    } finally {
      setReadMoveBusy(false);
    }
  }

  async function openReadMoveMenu() {
    if (!selected) return;
    const next = !readMoveMenuOpen;
    setReadMoveMenuOpen(next);
    if (!next) return;
    try {
      const res = await api.folders(selected.accountId);
      setReadMoveFolders(res.folders);
    } catch {
      setReadMoveFolders(folders);
    }
  }

  function snoozeMessage(msg: MailMessageSummary, wakeAt: number) {
    const k = keyOf(msg);
    const next = { ...snoozed, [k]: wakeAt };
    setSnoozed(next);
    saveSnoozed(next);
    setSnoozeMenuKey(null);
    if (selected && keyOf(selected) === k) {
      setSelected(null);
      setDetail(null);
    }
    toast.push({
      message: `Posticipato fino a ${formatSnoozeUntil(wakeAt)}`,
      actionLabel: "Annulla",
      onAction: () => {
        setSnoozed((prev) => {
          const copy = { ...prev };
          delete copy[k];
          saveSnoozed(copy);
          return copy;
        });
        void refreshMessages({ bustCache: true });
      },
    });
  }

  async function archiveSelected() {
    await moveSelected("archive", "Archiviato");
  }
  async function trashSelected() {
    await moveSelected("trash", "Spostato nel cestino");
  }
  async function spamSelected() {
    await moveSelected("spam", "Segnato come spam");
  }

  async function bulkAction(
    action: "seen" | "unseen" | "star" | "unstar" | "trash" | "spam" | "archive" | "move",
    targetFolder?: string,
  ) {
    const items = messages.filter((m) => selectedIds.has(keyOf(m)));
    if (!items.length) return;
    if (action === "move" && !targetFolder) return;
    if (action === "trash") {
      const ok = await confirm({
        title: "Sposta nel cestino",
        message: `Spostare ${items.length} messaggi nel cestino?`,
        confirmLabel: "Nel cestino",
        tone: "danger",
      });
      if (!ok) return;
    }
    if (action === "spam") {
      const ok = await confirm({
        title: "Segna come spam",
        message: `Segnare ${items.length} messaggi come spam?`,
        confirmLabel: "Spam",
        tone: "warn",
      });
      if (!ok) return;
    }

    const keys = new Set(items.map(keyOf));
    const snapshot = items;
    const touchingOpen = Boolean(selected && keys.has(keyOf(selected)));

    // Optimistic UI — remove/update instantly, sync IMAP in background
    setBulkBusy(true);
    setMoveMenuOpen(false);
    setSelectedIds(new Set());
    if (touchingOpen) {
      setSelected(null);
      setDetail(null);
    }

    if (action === "trash" || action === "spam" || action === "archive" || action === "move") {
      setMessages((prev) => prev.filter((m) => !keys.has(keyOf(m))));
      setTotal((t) => Math.max(0, t - items.length));
    } else if (action === "seen" || action === "unseen") {
      const seen = action === "seen";
      setMessages((prev) => prev.map((m) => (keys.has(keyOf(m)) ? { ...m, seen } : m)));
    } else if (action === "star" || action === "unstar") {
      const flagged = action === "star";
      setMessages((prev) => prev.map((m) => (keys.has(keyOf(m)) ? { ...m, flagged } : m)));
    }

    try {
      const res = await api.bulk({
        action,
        targetFolder,
        items: snapshot.map((m) => ({ accountId: m.accountId, folder: m.folder, uid: m.uid })),
      });
      const labels: Record<string, string> = {
        seen: "Segnate come lette",
        unseen: "Segnate come non lette",
        star: "Aggiunte ai preferiti",
        unstar: "Rimosse dai preferiti",
        trash: "Spostate nel cestino",
        spam: "Segnate come spam",
        archive: "Archiviate",
        move: `Spostate in «${targetFolder}»`,
      };
      toast.push({
        message: `${labels[action] || "Fatto"} · ${res.processed}${res.failed ? ` · ${res.failed} falliti` : ""}`,
      });
      // Soft refresh folder badges only (no full list reload) — list already updated
      if (selectedAccountId !== "all") {
        void loadFoldersFor(selectedAccountId);
      }
      if (res.failed > 0) {
        void refreshMessages({ bustCache: true });
      }
    } catch (e) {
      toast.push({ message: e instanceof Error ? e.message : String(e) });
      void refreshMessages({ bustCache: true });
    } finally {
      setBulkBusy(false);
    }
  }

  function toggleSelectAllVisible() {
    const keys = viewMessages.map(keyOf);
    const allSelected = keys.length > 0 && keys.every((k) => selectedIds.has(k));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allSelected) {
        for (const k of keys) next.delete(k);
      } else {
        for (const k of keys) next.add(k);
      }
      return next;
    });
  }

  const selectedAccountForFolders = useMemo(() => {
    if (selectedAccountId !== "all") return accounts.find((a) => a.id === selectedAccountId) || null;
    const items = messages.filter((m) => selectedIds.has(keyOf(m)));
    const ids = [...new Set(items.map((m) => m.accountId))];
    if (ids.length === 1) return accounts.find((a) => a.id === ids[0]) || null;
    return null;
  }, [selectedAccountId, accounts, messages, selectedIds]);

  const viewMessages = useMemo(() => {
    const now = new Date();
    const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());
    const inRange = (iso: string | null) => {
      if (dateFilter === "all") return true;
      if (!iso) return false;
      const d = new Date(iso);
      if (Number.isNaN(d.getTime())) return false;
      const today = startOfDay(now);
      if (dateFilter === "today") return d >= today;
      if (dateFilter === "yesterday") {
        const y = new Date(today);
        y.setDate(y.getDate() - 1);
        return d >= y && d < today;
      }
      if (dateFilter === "7d") {
        const from = new Date(today);
        from.setDate(from.getDate() - 7);
        return d >= from;
      }
      if (dateFilter === "30d") {
        const from = new Date(today);
        from.setDate(from.getDate() - 30);
        return d >= from;
      }
      if (dateFilter === "month") {
        return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      }
      if (dateFilter === "custom") {
        const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
        const to = dateTo ? new Date(`${dateTo}T23:59:59`) : null;
        if (from && d < from) return false;
        if (to && d > to) return false;
        return true;
      }
      return true;
    };

    const filtered = messages.filter((m) => inRange(m.date) && !isSnoozed(keyOf(m), snoozed));
    const fromKey = (m: MailMessageSummary) => (m.from || m.accountEmail || "").toLowerCase();
    const subKey = (m: MailMessageSummary) => (m.subject || "").toLowerCase();
    const dateKey = (m: MailMessageSummary) => (m.date ? Date.parse(m.date) : 0);

    filtered.sort((a, b) => {
      switch (sortBy) {
        case "date-asc":
          return dateKey(a) - dateKey(b);
        case "from-asc":
          return fromKey(a).localeCompare(fromKey(b), "it") || dateKey(b) - dateKey(a);
        case "from-desc":
          return fromKey(b).localeCompare(fromKey(a), "it") || dateKey(b) - dateKey(a);
        case "subject-asc":
          return subKey(a).localeCompare(subKey(b), "it") || dateKey(b) - dateKey(a);
        case "subject-desc":
          return subKey(b).localeCompare(subKey(a), "it") || dateKey(b) - dateKey(a);
        case "date-desc":
        default:
          return dateKey(b) - dateKey(a);
      }
    });
    return filtered;
  }, [messages, sortBy, dateFilter, dateFrom, dateTo, snoozed]);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    function onScroll() {
      if (!el || !hasMore || loadingMore || busy) return;
      const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 280;
      if (nearBottom) void loadMoreMessages();
    }
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, [hasMore, loadingMore, busy, selectedAccountId, page, viewMessages.length]);

  const virtualizer = useVirtualizer({
    count: viewMessages.length,
    getScrollElement: () => listRef.current,
    /* icon+label quick actions + mailbox chip must fit without clipping on hover */
    estimateSize: () => 128,
    overscan: 14,
  });

  const accountLabel =
    selectedAccountId === "all"
      ? "All accounts"
      : accounts.find((a) => a.id === selectedAccountId)?.email || "Account";

  const safeHtml = useMemo(() => {
    if (!detail?.html) return null;
    ensureMailPurifyHooks();
    return DOMPurify.sanitize(detail.html, {
      USE_PROFILES: { html: true },
      FORBID_TAGS: ["script", "iframe", "object", "embed", "form"],
      ADD_ATTR: ["target", "rel"],
    });
  }, [detail]);

  const threaded = useMemo(() => groupThreads(viewMessages), [viewMessages]);

  return (
    <>
      {(blockingLoad || busy) && (
        <div className={`mail-progress ${blockingLoad ? "blocking" : ""}`} aria-hidden>
          <div className="mail-progress-bar" />
        </div>
      )}
      {blockingLoad && (
        <div className="mail-block-overlay" aria-busy="true" aria-live="polite">
          <span>Caricamento casella…</span>
        </div>
      )}
      <div
        ref={workspaceRef}
        className={`workspace show-${mobile} ${railExpanded ? "rail-open" : "rail-closed"}`}
        style={
          {
            "--folder": `${folderWidth}px`,
            "--list": `${listWidth}px`,
          } as CSSProperties
        }
      >
        <aside className={`pane rail ${railExpanded ? "expanded" : "collapsed"}`}>
          <button
            className="rail-toggle"
            title={railExpanded ? "Comprimi account" : "Espandi account"}
            onClick={() => setRailExpanded((v) => !v)}
            aria-expanded={railExpanded}
          >
            <span className="rail-toggle-icon">{railExpanded ? "‹" : "›"}</span>
            {railExpanded && <span>Account</span>}
          </button>
          <button
            className={`rail-item ${selectedAccountId === "all" ? "active" : ""}`}
            title="Tutti gli account"
            onClick={() => selectAccount("all")}
          >
            <span className="rail-avatar all">∀</span>
            {railExpanded && (
              <span className="rail-meta">
                <strong>Tutti</strong>
                <em>{accounts.length} account</em>
              </span>
            )}
          </button>
          <div className="rail-scroll">
            {accounts.map((a) => (
              <button
                key={a.id}
                type="button"
                draggable
                className={`rail-item ${selectedAccountId === a.id ? "active" : ""} ${
                  dragOverId === a.id ? "drag-over" : ""
                } ${dragAccountId === a.id ? "dragging" : ""}`}
                title={`${a.email} · trascina per riordinare`}
                onDragStart={(e) => {
                  setDragAccountId(a.id);
                  e.dataTransfer.effectAllowed = "move";
                  e.dataTransfer.setData("text/plain", a.id);
                }}
                onDragEnd={() => {
                  setDragAccountId(null);
                  setDragOverId(null);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = "move";
                  setDragOverId(a.id);
                }}
                onDragLeave={() => {
                  setDragOverId((cur) => (cur === a.id ? null : cur));
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  void onAccountDrop(a.id);
                }}
                onClick={() => selectAccount(a.id)}
              >
                <span className="rail-avatar" style={{ background: a.color }}>
                  {(a.displayName || a.email).slice(0, 1).toUpperCase()}
                </span>
                {railExpanded && (
                  <span className="rail-meta">
                    <strong>{a.displayName || a.email.split("@")[0]}</strong>
                    <em>{a.email}</em>
                  </span>
                )}
              </button>
            ))}
          </div>
          <button className="rail-item rail-add" title="Aggiungi account" onClick={() => setWizard(true)}>
            <span className="rail-avatar add">+</span>
            {railExpanded && (
              <span className="rail-meta">
                <strong>Aggiungi</strong>
                <em>Nuovo account</em>
              </span>
            )}
          </button>
        </aside>

        <aside className="pane folders">
          <div className="pane-head folders-head">
            <div>
              <div className="eyebrow">Mailbox</div>
              <strong>{accountLabel}</strong>
            </div>
          </div>
          <button type="button" className="btn btn-primary btn-compose-wide" onClick={() => openCompose("new")}>
            Scrivi
          </button>
          <div className="folders-body">
            <div className="eyebrow" style={{ padding: "0.35rem 0.65rem" }}>
              Smart
            </div>
            {(
              [
                ["", "All in folder"],
                ["unread", "Unread"],
                ["starred", "Starred"],
                ["attachments", "Attachments"],
                ["today", "Today"],
              ] as Array<[SmartId, string]>
            ).map(([id, label]) => (
              <button
                key={id || "all"}
                className={`smart-btn ${smart === id ? "active" : ""}`}
                onClick={() => {
                  setSmart(id);
                  setMobile("list");
                }}
              >
                {label}
              </button>
            ))}
            <div className="eyebrow" style={{ padding: "0.75rem 0.75rem 0.35rem", display: "flex", alignItems: "center", justifyContent: "space-between", gap: "0.5rem" }}>
              <span>Cartelle</span>
              {selectedAccountId !== "all" && (
                <button
                  className="btn btn-ghost btn-sm"
                  type="button"
                  title="Gestisci cartelle"
                  onClick={() => setFolderMgrOpen(true)}
                >
                  Gestisci
                </button>
              )}
            </div>
            <div className="folder-list">
              {folders.map((f) => (
                <button
                  key={f.path}
                  className={`folder-btn ${folder === f.path && !smart ? "active" : ""}`}
                  onClick={() => {
                    setFolder(f.path);
                    setSmart("");
                    setSelected(null);
                    setDetail(null);
                    setMobile("list");
                  }}
                >
                  <span>{f.name}</span>
                  {typeof f.status?.unseen === "number" && f.status.unseen > 0 && (
                    <em className="badge">{f.status.unseen}</em>
                  )}
                </button>
              ))}
            </div>
          </div>
          <div
            className="col-resizer"
            title="Trascina per ridimensionare"
            onMouseDown={(e) => startColResize("folder", e)}
          />
        </aside>

        <section className="pane list">
          <div className="pane-head list-toolbar">
            <input
              className="input"
              placeholder="Cerca mail"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void refreshMessages({ bustCache: true })}
            />
            <button
              className="btn btn-refresh"
              type="button"
              title="Ricarica messaggi"
              onClick={() => void refreshMessages({ bustCache: true })}
              disabled={busy || blockingLoad}
            >
              {busy ? "Carico…" : "Ricarica"}
            </button>
          </div>
          <div className="list-filters">
            <label className="filter-field">
              <span>Ordina</span>
              <select
                className="select"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortId)}
              >
                <option value="date-desc">Data · più recenti</option>
                <option value="date-asc">Data · più vecchie</option>
                <option value="from-asc">Mittente A → Z</option>
                <option value="from-desc">Mittente Z → A</option>
                <option value="subject-asc">Oggetto A → Z</option>
                <option value="subject-desc">Oggetto Z → A</option>
              </select>
            </label>
            <label className="filter-field">
              <span>Periodo</span>
              <select
                className="select"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value as DateFilterId)}
              >
                <option value="all">Tutte le date</option>
                <option value="today">Oggi</option>
                <option value="yesterday">Ieri</option>
                <option value="7d">Ultimi 7 giorni</option>
                <option value="30d">Ultimi 30 giorni</option>
                <option value="month">Questo mese</option>
                <option value="custom">Intervallo personalizzato</option>
              </select>
            </label>
            {dateFilter === "custom" && (
              <>
                <label className="filter-field">
                  <span>Dal</span>
                  <input className="input" type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
                </label>
                <label className="filter-field">
                  <span>Al</span>
                  <input className="input" type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
                </label>
              </>
            )}
            {(dateFilter !== "all" || sortBy !== "date-desc") && (
              <button
                className="btn btn-ghost"
                type="button"
                onClick={() => {
                  setSortBy("date-desc");
                  setDateFilter("all");
                  setDateFrom("");
                  setDateTo("");
                }}
              >
                Reset filtri
              </button>
            )}
          </div>
          {selectedIds.size > 0 && (
            <div className="selection-bar">
              <div className="selection-bar-main">
                <label className="selection-check">
                  <input
                    type="checkbox"
                    checked={viewMessages.length > 0 && viewMessages.every((m) => selectedIds.has(keyOf(m)))}
                    onChange={toggleSelectAllVisible}
                  />
                  <strong>{selectedIds.size} selezionate</strong>
                </label>
                <button className="btn btn-ghost btn-sm" type="button" onClick={() => setSelectedIds(new Set())}>
                  Annulla
                </button>
              </div>
              <div className="selection-bar-actions">
                <button className="btn btn-sm" type="button" disabled={bulkBusy} onClick={() => void bulkAction("seen")}>
                  Segna come lette
                </button>
                <button className="btn btn-sm" type="button" disabled={bulkBusy} onClick={() => void bulkAction("unseen")}>
                  Segna come da leggere
                </button>
                <button className="btn btn-sm" type="button" disabled={bulkBusy} onClick={() => void bulkAction("star")}>
                  Preferiti
                </button>
                <button className="btn btn-sm" type="button" disabled={bulkBusy} onClick={() => void bulkAction("unstar")}>
                  Togli ★
                </button>
                <button className="btn btn-sm" type="button" disabled={bulkBusy} onClick={() => void bulkAction("archive")}>
                  Archivia
                </button>
                <div className="selection-move">
                  <button
                    className="btn btn-sm"
                    type="button"
                    disabled={bulkBusy || !selectedAccountForFolders}
                    title={
                      !selectedAccountForFolders
                        ? "Seleziona messaggi di un solo account per spostare"
                        : "Sposta in cartella"
                    }
                    onClick={() => {
                      void (async () => {
                        if (!selectedAccountForFolders) return;
                        const next = !moveMenuOpen;
                        setMoveMenuOpen(next);
                        if (next) await loadFoldersFor(selectedAccountForFolders.id);
                      })();
                    }}
                  >
                    Sposta ▾
                  </button>
                  {moveMenuOpen && selectedAccountForFolders && (
                    <div className="selection-move-menu" role="menu">
                      <div className="eyebrow" style={{ padding: "0.35rem 0.5rem" }}>
                        Sposta in… · {selectedAccountForFolders.email}
                      </div>
                      <div className="selection-move-list">
                        {folders
                          .filter((f) => f.path !== folder)
                          .map((f) => (
                            <button
                              key={f.path}
                              type="button"
                              className="selection-move-item"
                              disabled={bulkBusy}
                              onClick={() => void bulkAction("move", f.path)}
                            >
                              {f.path}
                            </button>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
                <button className="btn btn-sm" type="button" disabled={bulkBusy} onClick={() => void bulkAction("spam")}>
                  Spam
                </button>
                <button className="btn btn-danger btn-sm" type="button" disabled={bulkBusy} onClick={() => void bulkAction("trash")}>
                  Elimina
                </button>
              </div>
            </div>
          )}
          {error && <div className="error" style={{ padding: "0.6rem 1rem" }}>{error}</div>}
          <div style={{ padding: "0.4rem 1rem", color: "var(--muted)", fontSize: "0.78rem", display: "flex", gap: "0.75rem", alignItems: "center", flexWrap: "wrap" }}>
            <span>
              {viewMessages.length}
              {viewMessages.length !== total ? ` / ${total}` : ""} messaggi ·{" "}
              <strong style={{ color: "var(--unread)" }}>
                {viewMessages.filter((m) => !m.seen).length} non lette
              </strong>
              {" · "}
              {viewMessages.filter((m) => m.seen).length} lette · {folder}
              {smart ? ` · smart:${smart}` : ""}
              {dateFilter !== "all" ? ` · periodo:${dateFilter}` : ""}
            </span>
            {selectedAccountId !== "all" && (
              <div className="list-seen-actions">
                {viewMessages.some((m) => !m.seen) && (
                  <button
                    className="btn btn-sm"
                    type="button"
                    disabled={busy || blockingLoad}
                    onClick={() => {
                      void (async () => {
                        const ok = await confirm({
                          title: "Segna cartella come letta",
                          message: `Segnare come lette tutte le email non lette in «${folder}» su questo account? L’operazione scrive su IMAP.`,
                          confirmLabel: "Segna come lette",
                          tone: "default",
                        });
                        if (!ok) return;
                        try {
                          setBusy(true);
                          const r = await api.markFolderSeen(selectedAccountId, folder);
                          toast.push({ message: `Segnate come lette: ${r.marked}` });
                          await refreshMessages({ bustCache: true });
                        } catch (e) {
                          toast.push({ message: e instanceof Error ? e.message : String(e) });
                        } finally {
                          setBusy(false);
                        }
                      })();
                    }}
                  >
                    Segna come letta
                  </button>
                )}
                <button
                  className="btn btn-sm"
                  type="button"
                  disabled={busy || blockingLoad || bulkBusy}
                  title={
                    selectedIds.size > 0
                      ? "Segna le selezionate come da leggere"
                      : selected?.seen
                        ? "Segna il messaggio aperto come da leggere"
                        : "Seleziona messaggi letti, oppure apri un messaggio letto"
                  }
                  onClick={() => {
                    void (async () => {
                      if (selectedIds.size > 0) {
                        await bulkAction("unseen");
                        return;
                      }
                      if (selected?.seen) {
                        await setSeenState(false);
                        return;
                      }
                      toast.push({
                        message: "Seleziona una o più email lette, oppure aprine una letta",
                      });
                    })();
                  }}
                >
                  Segna come da leggere
                </button>
              </div>
            )}
          </div>
          <div className="msg-list-stack">
          <div className="msg-scroll" ref={listRef}>
            <div style={{ height: virtualizer.getTotalSize(), position: "relative" }}>
              {virtualizer.getVirtualItems().map((row) => {
                const msg = viewMessages[row.index]!;
                const active = selected && keyOf(selected) === keyOf(msg);
                const checked = selectedIds.has(keyOf(msg));
                const accountColor =
                  accounts.find((a) => a.id === msg.accountId)?.color || "var(--accent)";
                return (
                  <div
                    key={keyOf(msg)}
                    className={`msg-item ${checked ? "is-checked" : ""} ${active ? "is-active" : ""}`}
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      width: "100%",
                      height: `${row.size}px`,
                      transform: `translateY(${row.start}px)`,
                    }}
                  >
                    <button
                      type="button"
                      className={`msg-select-hit ${checked ? "is-selected" : ""}`}
                      aria-pressed={checked}
                      aria-label={checked ? "Deseleziona messaggio" : "Seleziona messaggio"}
                      title={checked ? "Deseleziona" : "Seleziona per azioni di gruppo"}
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        setSelectedIds((prev) => {
                          const next = new Set(prev);
                          const k = keyOf(msg);
                          if (next.has(k)) next.delete(k);
                          else next.add(k);
                          return next;
                        });
                      }}
                    >
                      <span className="msg-select-hand" aria-hidden>
                        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M8 13V6.5a1.5 1.5 0 0 1 3 0V12" />
                          <path d="M11 11.5V5.5a1.5 1.5 0 0 1 3 0V12" />
                          <path d="M14 12V7.5a1.5 1.5 0 0 1 3 0V14" />
                          <path d="M17 13.5v-1a1.5 1.5 0 0 1 3 0V16a6 6 0 0 1-6 6h-1.5a7 7 0 0 1-6.3-4L5 14.5a1.7 1.7 0 0 1 2.4-2.3L9 13" />
                        </svg>
                      </span>
                      <span className="msg-select-label">select</span>
                    </button>
                    <button
                      className={`msg-row ${active ? "active" : ""} ${msg.seen ? "" : "unread"}`}
                      onClick={() => void openMessage(msg)}
                    >
                      <div className="msg-top">
                        <span
                          className={`msg-avatar ${msg.seen ? "is-read" : "is-unread"}`}
                          aria-hidden
                          title={msg.seen ? "Letta" : "Da leggere"}
                        >
                          {(msg.from || msg.accountEmail || "?")
                            .replace(/^"|"$/g, "")
                            .trim()
                            .charAt(0)
                            .toUpperCase()}
                        </span>
                        <div className="msg-from-wrap">
                          <strong className="msg-from">{msg.from || msg.accountEmail}</strong>
                          {msg.hasAttachments ? (
                            <span className="msg-attach" title="Ha allegati" aria-label="Allegati">
                              <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                              </svg>
                            </span>
                          ) : null}
                        </div>
                        <div className="msg-top-end">
                          {msg.answered ? (
                            <span
                              className="msg-answered"
                              title="Hai già risposto a questo messaggio"
                              aria-label="Già risposto"
                            >
                              <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                                <path d="M9 17H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v3" />
                                <path d="M14 15l-4 4 4 4" />
                                <path d="M10 19h9a2 2 0 0 0 2-2v-4" />
                              </svg>
                              <em className="msg-answered-tip">Già risposto</em>
                            </span>
                          ) : null}
                          <time>
                            {msg.date
                              ? new Date(msg.date).toLocaleString(undefined, {
                                  month: "short",
                                  day: "numeric",
                                  hour: "2-digit",
                                  minute: "2-digit",
                                })
                              : ""}
                          </time>
                        </div>
                      </div>
                      <div className="sub">{msg.subject || "(senza oggetto)"}</div>
                      <div className="snip">{msg.snippet || "\u00a0"}</div>
                      <div className="msg-meta-foot">
                        <div className="msg-meta-foot-main">
                          <span
                            className="msg-mailbox"
                            title={`Casella · ${msg.accountEmail}`}
                            style={{
                              color: accountColor,
                              background: `color-mix(in srgb, ${accountColor} 16%, transparent)`,
                              borderColor: `color-mix(in srgb, ${accountColor} 45%, transparent)`,
                            }}
                          >
                            <span className="msg-mailbox-dot" style={{ background: accountColor }} />
                            {msg.accountEmail}
                          </span>
                          {msg.to ? (
                            <span className="msg-to" title={msg.to}>
                              <em>A</em> {msg.to}
                            </span>
                          ) : null}
                          {msg.flagged && (
                            <span className="msg-flags-inline">
                              <span className="flag-star" title="Stellato">★</span>
                            </span>
                          )}
                        </div>
                        <MsgRowQuickActions
                          seen={msg.seen}
                          snoozeOpen={snoozeMenuKey === keyOf(msg)}
                          snoozePresets={snoozePresetList}
                          onArchive={() => void moveMessage(msg, "archive", "Archiviato")}
                          onDelete={() => void moveMessage(msg, "trash", "Spostato nel cestino")}
                          onMarkUnread={() => void setMessageSeen(msg, false)}
                          onToggleSnooze={() =>
                            setSnoozeMenuKey((k) => (k === keyOf(msg) ? null : keyOf(msg)))
                          }
                          onSnoozePick={(wakeAt) => snoozeMessage(msg, wakeAt)}
                        />
                      </div>
                    </button>
                  </div>
                );
              })}
            </div>
            {viewMessages.length === 0 && !busy && (
              <div className="empty">
                <h2>{accounts.length ? "Nessun messaggio" : "Collega un account"}</h2>
                <p>
                  {accounts.length
                    ? dateFilter !== "all" || search.trim()
                      ? "Prova a cambiare periodo, ordinamento o ricerca."
                      : "Prova un’altra cartella o togli i filtri smart."
                    : "Aggiungi Gmail, Microsoft o IMAP per iniziare."}
                </p>
                {!accounts.length && (
                  <button className="btn btn-primary" onClick={() => setWizard(true)}>
                    Add account
                  </button>
                )}
              </div>
            )}
            {loadingMore && (
              <div className="msg-load-more" aria-live="polite">
                Carico messaggi più vecchi…
              </div>
            )}
          </div>
          {viewMessages.length > 0 && (
            <div className="msg-load-bar">
              {hasMore ? (
                <button
                  type="button"
                  className="btn btn-primary btn-sm msg-load-btn"
                  disabled={loadingMore || busy}
                  onClick={() => void loadMoreMessages()}
                >
                  {loadingMore ? "Caricamento…" : "Carica messaggi più vecchi"}
                </button>
              ) : (
                <span className="msg-load-end">Fine lista · {total || viewMessages.length} messaggi</span>
              )}
            </div>
          )}
          </div>
          <div
            className="col-resizer"
            title="Trascina per ridimensionare"
            onMouseDown={(e) => startColResize("list", e)}
          />
        </section>

        <section className="pane read">
          {!detail || !selected ? (
            <div className="empty read-empty">
              <h2>Seleziona un messaggio</h2>
              <p>j/k naviga · c scrivi · r rispondi · a a tutti · f inoltra · u letto/non letto · e archivia</p>
            </div>
          ) : (
            <div className="read-scroll">
              <div className="read-head">
                <div className="read-toolbar">
                  <button className="btn btn-ghost read-back" onClick={() => setMobile("list")}>
                    ← Lista
                  </button>
                  <div className="read-actions">
                    {selected && isDraftsFolder(selected.folder) ? (
                      <button
                        className="action-btn primary"
                        type="button"
                        onClick={() => openCompose("draft")}
                        title="Modifica bozza"
                      >
                        <span className="action-ico">✎</span>
                        <span>Modifica</span>
                      </button>
                    ) : (
                      <>
                        <button className="action-btn primary" type="button" onClick={() => openCompose("reply")} title="Rispondi (r)">
                          <span className="action-ico">↩</span>
                          <span>Rispondi</span>
                        </button>
                        <button className="action-btn" type="button" onClick={() => openCompose("replyAll")} title="Rispondi a tutti (a)">
                          <span className="action-ico">⇉</span>
                          <span>A tutti</span>
                        </button>
                        <button className="action-btn primary" type="button" onClick={() => openCompose("forward")} title="Inoltra (f)">
                          <span className="action-ico">➜</span>
                          <span>Inoltra</span>
                        </button>
                      </>
                    )}
                    <button className="action-btn" type="button" onClick={() => void toggleStar()} title="Preferito (s)">
                      <span className="action-ico">{selected.flagged ? "★" : "☆"}</span>
                      <span>{selected.flagged ? "Togli stella" : "Stella"}</span>
                    </button>
                    <button className="action-btn" type="button" onClick={() => void archiveSelected()} title="Archivia (e)">
                      <span className="action-ico">▤</span>
                      <span>Archivia</span>
                    </button>
                    <div className="read-move-wrap">
                      <button
                        className={`action-btn ${readMoveMenuOpen ? "primary" : ""}`}
                        type="button"
                        disabled={readMoveBusy}
                        onClick={() => void openReadMoveMenu()}
                        title="Sposta in cartella"
                      >
                        <span className="action-ico">📁</span>
                        <span>Cartella</span>
                      </button>
                      {readMoveMenuOpen && (
                        <div className="read-move-menu" role="menu">
                          <div className="eyebrow" style={{ padding: "0.35rem 0.55rem" }}>
                            Sposta in…
                          </div>
                          <div className="read-move-list">
                            {(readMoveFolders.length ? readMoveFolders : folders)
                              .filter((f) => f.path !== selected.folder)
                              .map((f) => (
                                <button
                                  key={f.path}
                                  type="button"
                                  className="read-move-item"
                                  disabled={readMoveBusy}
                                  onClick={() => void moveToFolder(selected, f.path)}
                                >
                                  {f.name || f.path}
                                  {f.path !== f.name ? (
                                    <em className="read-move-path">{f.path}</em>
                                  ) : null}
                                </button>
                              ))}
                          </div>
                        </div>
                      )}
                    </div>
                    <button
                      className={`action-btn ${selected.seen ? "" : "primary"}`}
                      type="button"
                      disabled={!selected.seen}
                      onClick={() => void setSeenState(false)}
                      title="Segna come da leggere (u)"
                    >
                      <span className="action-ico">○</span>
                      <span>Da leggere</span>
                    </button>
                    <button
                      className={`action-btn ${selected.seen ? "primary" : ""}`}
                      type="button"
                      disabled={selected.seen}
                      onClick={() => void setSeenState(true)}
                      title="Segna come letta"
                    >
                      <span className="action-ico">●</span>
                      <span>Letta</span>
                    </button>
                    <button className="action-btn warn" type="button" onClick={() => void spamSelected()} title="Spam (!)">
                      <span className="action-ico">⚠</span>
                      <span>Spam</span>
                    </button>
                    <button className="action-btn danger" type="button" onClick={() => void trashSelected()} title="Elimina (#)">
                      <span className="action-ico">⌫</span>
                      <span>Elimina</span>
                    </button>
                  </div>
                </div>
                <h1>{detail.subject || "(senza oggetto)"}</h1>
                <div className="read-identity">
                  <span
                    className="rail-avatar"
                    style={{
                      background: accounts.find((a) => a.id === selected.accountId)?.color || "var(--accent)",
                    }}
                  >
                    {(detail.from || "?").replace(/"/g, "").trim().slice(0, 1).toUpperCase()}
                  </span>
                  <div className="meta">
                    <div>
                      <span>Da</span> {detail.from}
                    </div>
                    <div>
                      <span>A</span> {detail.to}
                    </div>
                    <div>
                      <span>Data</span> {detail.date ? new Date(detail.date).toLocaleString() : "—"}
                    </div>
                  </div>
                </div>
              </div>
              {detail.attachments?.length > 0 && (
                <div className="read-attachments">
                  <div className="read-attachments-label">Allegati ({detail.attachments.length})</div>
                  <ul className="read-attachments-list">
                    {detail.attachments.map((att) => (
                      <li key={att.partId}>
                        <button
                          type="button"
                          className="read-attachment-btn"
                          onClick={() =>
                            setPreviewAttachment({
                              accountId: selected.accountId,
                              folder: selected.folder,
                              uid: selected.uid,
                              attachment: att,
                            })
                          }
                        >
                          <span className="read-attachment-icon">📎</span>
                          <span className="read-attachment-name">{att.filename}</span>
                          <span className="read-attachment-size">
                            {att.size >= 1024 * 1024
                              ? `${(att.size / (1024 * 1024)).toFixed(1)} MB`
                              : att.size >= 1024
                                ? `${(att.size / 1024).toFixed(1)} KB`
                                : `${att.size} B`}
                          </span>
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="read-body">
                {safeHtml ? (
                  <div className="read-html" dangerouslySetInnerHTML={{ __html: safeHtml }} />
                ) : (
                  <pre className="read-plain">{detail.text || "(vuoto)"}</pre>
                )}
              </div>
            </div>
          )}
        </section>
      </div>

      <nav className="mobile-nav" aria-label="Navigazione mobile">
        <button
          className={mobile === "nav" ? "active" : ""}
          onClick={() => {
            setRailExpanded(true);
            setMobile("nav");
          }}
          type="button"
        >
          Account
        </button>
        <button className={mobile === "list" ? "active" : ""} onClick={() => setMobile("list")} type="button">
          Inbox
        </button>
        <button className={mobile === "read" ? "active" : ""} onClick={() => setMobile("read")} type="button">
          Leggi
        </button>
        <button type="button" onClick={() => openCompose("new")}>
          Scrivi
        </button>
        <button type="button" onClick={() => setWizard(true)}>
          +
        </button>
      </nav>

      {previewAttachment && (
        <AttachmentPreviewModal
          accountId={previewAttachment.accountId}
          folder={previewAttachment.folder}
          uid={previewAttachment.uid}
          attachment={previewAttachment.attachment}
          onClose={() => setPreviewAttachment(null)}
        />
      )}
      {composer && (
        <ComposerModal
          accounts={accounts}
          maxAttachmentSizeMb={prefs?.maxAttachmentSizeMb ?? 35}
          defaultAccountId={
            composeMode === "new"
              ? selectedAccountId !== "all"
                ? selectedAccountId
                : accounts[0]?.id
              : detail?.accountId || (selectedAccountId !== "all" ? selectedAccountId : accounts[0]?.id)
          }
          mode={composeMode}
          source={composeMode === "new" ? null : detail}
          onClose={() => {
            setComposer(false);
            if (composeMode === "draft") void refreshMessages({ bustCache: true });
          }}
          onSent={() => {
            setComposer(false);
            toast.push({ message: "Messaggio inviato" });
            if ((composeMode === "reply" || composeMode === "replyAll") && detail) {
              const k = messageKey(detail);
              setMessages((prev) => prev.map((m) => (messageKey(m) === k ? { ...m, answered: true } : m)));
              setSelected((s) => (s && messageKey(s) === k ? { ...s, answered: true } : s));
              setDetail((d) => (d ? { ...d, answered: true } : d));
            }
            void refreshMessages({ bustCache: true });
          }}
        />
      )}
      {wizard && (
        <AccountWizard
          onClose={() => setWizard(false)}
          onAdded={async () => {
            setWizard(false);
            await refreshAccounts();
            toast.push({ message: "Account connected" });
          }}
        />
      )}
      {folderMgrOpen && selectedAccountId !== "all" && accounts.find((a) => a.id === selectedAccountId) && (
        <FolderManagerModal
          account={accounts.find((a) => a.id === selectedAccountId)!}
          folders={folders}
          onClose={() => setFolderMgrOpen(false)}
          onChanged={(next) => {
            setFolders(next);
            if (!next.some((f) => f.path === folder)) {
              setFolder("INBOX");
            }
            void refreshMessages({ bustCache: true });
          }}
        />
      )}
    </>
  );
}

function groupThreads(messages: MailMessageSummary[]) {
  // Lightweight thread estimate by normalized subject
  const map = new Map<string, number>();
  for (const m of messages) {
    const key = (m.subject || "")
      .replace(/^(re|fw|fwd):\s*/gi, "")
      .trim()
      .toLowerCase();
    map.set(key, (map.get(key) || 0) + 1);
  }
  return [...map.entries()];
}
